import { DOMParser } from '@tiptap/pm/model';
import { elementFromString } from '@tiptap/react';
import React, { useContext } from 'react';

import { EditorContext } from './../../../ricos-context';

import { isOfType } from '../../ts-utils';
import type {
  AiPluginEditorConfig,
  InitialPromptOptions,
  Prompt,
  ResultContent,
  ResultEntry,
} from '../../types';
import { convertNewLinesToParagraphs } from '../../utils/convert-new-lines-to-paragraphs';
import { scrollToElementIfNeeded } from '../../utils/scroll-to-element-if-needed';
import type { RequestPrompt } from '../../utils/use-ai-request-service';
import { useAiRequestService } from '../../utils/use-ai-request-service';
import type { TextualPromptInputAreaRef } from '../textual-prompt-input-area/textual-prompt-input-area';
import type { AiModalAction, AiModalState } from './types';

type UseModalActionsProps = {
  state: AiModalState;
  dispatch: React.Dispatch<AiModalAction>;
  inputRef: React.RefObject<TextualPromptInputAreaRef | null>;
  listRef: React.RefObject<any | null>;
  selection: string;
  initialPromptOptions?: InitialPromptOptions;
} & Pick<AiPluginEditorConfig, 'getGatewayBaseUrl' | 'appDefId'>;

export const useAiModalActions = ({
  state,
  dispatch,
  getGatewayBaseUrl,
  appDefId,
  inputRef,
  listRef,
  selection,
  initialPromptOptions,
}: UseModalActionsProps) => {
  const { adapter } = useContext(EditorContext);
  const config = React.useMemo(
    () => ({ getGatewayBaseUrl, appDefId }),
    [getGatewayBaseUrl, appDefId]
  );
  const { fetchPrompt } = useAiRequestService(config);

  const requestPromptData = React.useCallback(
    async (prompt: RequestPrompt, content: string): Promise<ResultContent[]> => {
      const response = await fetchPrompt(prompt, content);

      if (response.type === 'ERROR') {
        throw new Error(response.error);
      }

      const suggestionsAsHTML: string[] = response.results;

      const schema = adapter.tiptapEditor.schema;

      const parser = DOMParser.fromSchema(schema);

      return suggestionsAsHTML.map(s => {
        const parsedContent = parser.parse(elementFromString(convertNewLinesToParagraphs(s)));
        return {
          content: parsedContent.toJSON(),
          plainContent: parsedContent.textContent,
          htmlContent: s,
        };
      });
    },
    []
  );

  const streamPromptDataToState = React.useCallback(
    (suggestions: ResultContent[], requestId: string) => {
      dispatch({
        type: 'FINISH_LOADING',
        payload: {
          suggestions,
          requestId,
        },
      });
      inputRef.current?.focus();
    },
    [dispatch]
  );

  const setSelectedPromptIndex = React.useCallback(
    (selectedPromptIndex: number, scrollToView = false) => {
      dispatch({
        type: 'SET_SELECTED_PROMPT_INDEX',
        payload: {
          selectedPromptIndex,
        },
      });

      if (listRef.current?.childNodes[selectedPromptIndex] && scrollToView) {
        scrollToElementIfNeeded(listRef.current?.childNodes[selectedPromptIndex], listRef.current);
      }
    },
    [listRef.current]
  );

  const setFreePromptValue = React.useCallback((value: string) => {
    dispatch({
      type: 'SET_FREE_PROMPT_VALUE',
      payload: {
        value,
      },
    });
  }, []);

  const submitTextualPrompt = React.useCallback(async (prompt: string, content: string) => {
    const requestId = Date.now().toString();
    dispatch({
      type: 'SUBMIT_TEXTUAL_PROMPT',
      payload: {
        requestId,
      },
    });

    try {
      const suggestions = await requestPromptData({ prompt }, content);
      inputRef.current?.focus();
      streamPromptDataToState(suggestions, requestId);
    } catch (e) {
      dispatch({
        type: 'SET_ERROR',
        payload: {
          requestPrompt: prompt,
          requestContent: content,
        },
      });
    }
  }, []);

  const submitPrompt = React.useCallback(async (prompt: Prompt, content = '') => {
    const requestId = Date.now().toString();
    dispatch({
      type: 'SUBMIT_PROMPT',
      payload: {
        prompt,
        requestId,
      },
    });

    try {
      const payload =
        'promptId' in prompt
          ? { promptId: prompt.promptId, promptParams: prompt.promptParams }
          : 'promptSlug' in prompt
          ? { promptSlug: prompt.promptSlug, promptParams: prompt.promptParams }
          : null;

      if (payload === null) {
        throw new Error('Invalid prompt');
      }

      const suggestions = await requestPromptData(payload, content);
      inputRef.current?.focus();
      streamPromptDataToState(suggestions, requestId);
    } catch (e) {
      dispatch({
        type: 'SET_ERROR',
        payload: {
          requestPrompt: prompt,
          requestContent: content,
        },
      });
    }
  }, []);

  const cancelPrompt = React.useCallback(() => {
    if (state.type === 'IN_PROGRESS') {
      inputRef.current?.focus();
    }

    dispatch({
      type: 'CANCEL_PROMPT',
    });
  }, [state]);

  const triggerFreePrompt = React.useCallback(() => {
    if (isOfType(['RESULTS'], state)) {
      if (state.textualPrompt.length) {
        const results = state.entries.filter(
          (entry): entry is ResultEntry => entry.type === 'result'
        );
        const lastResult = results[results.length - 1];
        return submitTextualPrompt(
          state.textualPrompt,
          lastResult?.response.htmlContent ?? selection
        );
      }
    }

    if (isOfType(['INITIAL'], state)) {
      if (state.initialPrompt) {
        return submitPrompt(
          initialPromptOptions?.promptPayloadFormatter
            ? initialPromptOptions?.promptPayloadFormatter(state.initialPrompt, state.textualPrompt)
            : state.initialPrompt,
          selection
        );
      }

      if (state.textualPrompt.length) {
        return submitTextualPrompt(state.textualPrompt, selection);
      }
    }
  }, [state]);

  const retryPrompt = React.useCallback(() => {
    if (!isOfType(['ERROR'], state)) {
      return;
    }

    if (typeof state.requestPrompt === 'string') {
      return submitTextualPrompt(state.requestPrompt, state.requestContent);
    }

    return submitPrompt(state.requestPrompt, state.requestContent);
  }, [state]);

  const goBack = React.useCallback(() => {
    dispatch({
      type: 'GO_BACK',
    });
  }, [state]);

  return {
    setSelectedPromptIndex,
    setFreePromptValue,
    submitPrompt,
    triggerFreePrompt,
    cancelPrompt,
    retryPrompt,
    goBack,
  };
};
